import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import AssetService from "../../../apis/AssetService";
import useDebouncedValue from "../../../utils/useDebouncedValue";
import AssetCard from "./AssetCard";
import AssetUploadDialog from "./AssetUploadDialog";
import AssetDetailsSidebar from "./AssetDetailsSidebar";

const AssetManager = () => {
  const [assets, setAssets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 350);
  const [selectedTags, setSelectedTags] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDeleteAsset, setConfirmDeleteAsset] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const [pageTokens, setPageTokens] = useState({ 0: null });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [renameAsset, setRenameAsset] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState(null);
  const [renameSaving, setRenameSaving] = useState(false);

  const tagOptions = useMemo(() => {
    const unique = new Set(selectedTags);
    assets.forEach((asset) => {
      (asset.tags || []).forEach((tag) => unique.add(tag));
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [assets, selectedTags]);

  const loadAssets = async ({ resetPage = false, targetPage = page } = {}) => {
    setLoading(true);
    setError(null);

    const nextPage = resetPage ? 0 : targetPage;
    const tokens = resetPage ? { 0: null } : pageTokens;
    const tokenForPage = Object.prototype.hasOwnProperty.call(tokens, nextPage)
      ? tokens[nextPage]
      : null;

    try {
      const response = await AssetService.listAssets({
        search: debouncedSearch || undefined,
        tags: selectedTags,
        limit: pageSize,
        nextToken: tokenForPage || undefined,
      });
      setAssets(response.items || []);
      setTotalCount(
        typeof response.totalCount === "number"
          ? response.totalCount
          : response.items?.length || 0
      );
      setPageTokens((previousTokens) => {
        const base = resetPage ? { 0: null } : { ...previousTokens };
        base[nextPage + 1] = response.nextToken || null;
        return base;
      });
      if (resetPage) {
        setPage(0);
      } else {
        setPage(nextPage);
      }
    } catch (fetchError) {
      console.error("Failed to load assets", fetchError);
      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          "Unable to load assets right now."
      );
      setAssets([]);
      setTotalCount(0);
      if (resetPage) {
        setPageTokens({ 0: null });
        setPage(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAssets({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedTags]);

  useEffect(() => {
    if (renameAsset) {
      setRenameValue(renameAsset.title || renameAsset.fileName || "");
      setRenameError(null);
    }
  }, [renameAsset]);

  useEffect(() => {
    if (!selectedAsset) {
      return;
    }

    const latest = (assets || []).find((asset) => asset.id === selectedAsset.id);

    if (!latest) {
      setSelectedAsset(null);
      return;
    }

    if (latest !== selectedAsset) {
      setSelectedAsset(latest);
    }
  }, [assets, selectedAsset]);

  const handleOpenUpload = () => setUploadOpen(true);
  const handleCloseUpload = () => setUploadOpen(false);

  const handleUploaded = (asset) => {
    setToastMessage("Asset uploaded successfully");
    setAssets((previous) => {
      const next = Array.isArray(previous) ? [...previous] : [];
      const index = next.findIndex((existing) => existing.id === asset?.id);
      if (index >= 0) {
        next[index] = { ...next[index], ...asset };
      } else if (asset) {
        next.unshift(asset);
      }
      return next;
    });
    setTotalCount((count) => count + 1);
  };

  const handleDelete = async () => {
    if (!confirmDeleteAsset?.id) {
      return;
    }

    try {
      await AssetService.deleteAsset(confirmDeleteAsset.id);
      setToastMessage("Asset deleted");
      const remainingAssets = (assets || []).filter(
        (asset) => asset.id !== confirmDeleteAsset.id
      );
      if (selectedAsset?.id === confirmDeleteAsset.id) {
        setSelectedAsset(null);
      }
      const shouldLoadPreviousPage = remainingAssets.length === 0 && page > 0;
      setAssets(remainingAssets);
      setTotalCount((count) => Math.max(0, count - 1));
      void loadAssets({ targetPage: shouldLoadPreviousPage ? page - 1 : page });
    } catch (deleteError) {
      console.error("Failed to delete asset", deleteError);
      setToastMessage(
        deleteError?.response?.data?.message ||
          deleteError?.message ||
          "Unable to delete asset"
      );
    } finally {
      setConfirmDeleteAsset(null);
    }
  };

  const handleCopyUrl = async (asset) => {
    if (!asset?.url) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(asset.url);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = asset.url;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setToastMessage("Asset URL copied to clipboard");
    } catch (copyError) {
      console.error("Failed to copy asset URL", copyError);
      setToastMessage("Unable to copy asset URL");
    }
  };

  const handleViewDetails = (asset) => {
    if (!asset) {
      setSelectedAsset(null);
      return;
    }

    setSelectedAsset(asset);
  };

  const handleStartRename = (asset) => {
    setRenameAsset(asset);
  };

  const handleCloseRenameDialog = () => {
    setRenameAsset(null);
    setRenameValue("");
    setRenameError(null);
    setRenameSaving(false);
  };

  const handleSubmitRename = async () => {
    if (!renameAsset?.id) {
      return;
    }

    const trimmedValue = renameValue.trim();

    if (!trimmedValue) {
      setRenameError("A name is required.");
      return;
    }

    setRenameSaving(true);
    setRenameError(null);
    try {
      const response = await AssetService.updateAsset(renameAsset.id, {
        title: trimmedValue,
      });
      const updatedAsset = {
        ...renameAsset,
        ...(response || {}),
        title: trimmedValue,
      };
      setAssets((previous) =>
        (previous || []).map((asset) =>
          asset.id === renameAsset.id ? { ...asset, ...updatedAsset } : asset
        )
      );
      setToastMessage("Asset renamed");
      if (selectedAsset?.id === renameAsset.id) {
        const mergedSelected = { ...selectedAsset, ...updatedAsset };
        setSelectedAsset(mergedSelected);
      }
      handleCloseRenameDialog();
    } catch (renameErrorInstance) {
      console.error("Failed to rename asset", renameErrorInstance);
      setRenameError(
        renameErrorInstance?.response?.data?.message ||
          renameErrorInstance?.message ||
          "Unable to rename asset."
      );
    } finally {
      setRenameSaving(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedTags([]);
  };

  const handleAssetUpdated = (updatedAsset) => {
    if (!updatedAsset?.id) {
      return;
    }

    setAssets((previous) =>
      (previous || []).map((asset) =>
        asset.id === updatedAsset.id ? { ...asset, ...updatedAsset } : asset
      )
    );
    setSelectedAsset((previous) =>
      previous?.id === updatedAsset.id ? { ...previous, ...updatedAsset } : previous
    );
  };

  const handleCloseSidebar = () => {
    setSelectedAsset(null);
  };

  const showClearFilters = searchTerm.length > 0 || selectedTags.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: { xs: 3, lg: 4 },
        alignItems: { lg: "flex-start" },
      }}
    >
      <Stack spacing={3} sx={{ flex: "1 1 auto", minWidth: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
      >
        <Stack spacing={0.5}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Asset library
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {totalCount === 1
              ? "1 asset"
              : `${totalCount.toLocaleString()} assets available`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {showClearFilters && (
            <Button onClick={handleClearFilters} variant="text">
              Clear filters
            </Button>
          )}
          <Tooltip title="Refresh assets">
            <span>
              <IconButton onClick={() => loadAssets()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenUpload}
          >
            Upload asset
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            placeholder="Search assets"
            label="Search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={selectedTags}
            onChange={(_, tagsValue) => setSelectedTags(tagsValue)}
            renderInput={(params) => (
              <TextField {...params} label="Filter by tags" placeholder="Type to add tag" />
            )}
            sx={{ minWidth: { md: 280 } }}
          />
        </Stack>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      {assets.length === 0 && !loading ? (
        <Alert severity="info">
          No assets match your filters yet. Upload a new asset or adjust the filters above.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "repeat(2, minmax(160px, 1fr))",
              sm: "repeat(auto-fill, minmax(160px, 1fr))",
            },
            justifyItems: "center",
          }}
        >
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onOpen={handleViewDetails}
              onDelete={setConfirmDeleteAsset}
              onCopyUrl={handleCopyUrl}
              onRename={handleStartRename}
              isSelected={selectedAsset?.id === asset.id}
            />
          ))}
        </Box>
      )}

      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => {
            if (newPage === page) {
              return;
            }
            void loadAssets({ targetPage: newPage });
          }}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onRowsPerPageChange={() => {
            // no-op required by component signature
          }}
          labelRowsPerPage="Assets per page"
          sx={{ alignSelf: "flex-end" }}
        />
      )}

      <AssetUploadDialog
        open={uploadOpen}
        onClose={handleCloseUpload}
        onUploaded={(asset) => {
          handleUploaded(asset);
          void loadAssets();
        }}
        existingTags={tagOptions}
      />

      <Dialog open={Boolean(renameAsset)} onClose={handleCloseRenameDialog}>
        <DialogTitle>Rename asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {renameError && <Alert severity="error">{renameError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              label="Asset name"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              disabled={renameSaving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog} disabled={renameSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRename}
            variant="contained"
            disabled={renameSaving}
          >
            {renameSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmDeleteAsset)}
        onClose={() => setConfirmDeleteAsset(null)}
      >
        <DialogTitle>Delete asset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "
            {confirmDeleteAsset?.title || confirmDeleteAsset?.fileName || "this asset"}"?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAsset(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={4000}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
      />
      </Stack>

      <AssetDetailsSidebar
        asset={selectedAsset}
        open={Boolean(selectedAsset)}
        onClose={handleCloseSidebar}
        onAssetChange={handleAssetUpdated}
        existingTags={tagOptions}
      />
    </Box>
  );
};

export default AssetManager;
